/**
 * VideoInsertDialog — 掲示板本文への動画挿入ダイアログ。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-3・§7-4
 *
 * 2 タブ構成:
 * - アップロード: 動画ファイル (100MB 以下) を Storage へアップロードし
 *   <video controls src> として挿入（BoardVideoNode）。
 * - YouTube URL: URL を extractYouTubeId で検証し、@tiptap/extension-youtube の
 *   setYoutubeVideo で <div data-youtube-video><iframe .../></div> を挿入。
 */
import { extractYouTubeId } from '@goenhub/landing-sections';
import type { Editor } from '@tiptap/react';
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { uploadBoardVideo } from '@/lib/boardMedia';

interface Props {
  postId: string;
  editor: Editor;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VideoInsertDialog({ postId, editor, open, onOpenChange }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [youtubeUrl, setYoutubeUrl] = useState('');

  const reset = () => {
    setUploading(false);
    setProgress(0);
    setYoutubeUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setProgress(0);
    try {
      const url = await uploadBoardVideo(postId, file, setProgress);
      editor.chain().focus().setBoardVideo({ src: url }).run();
      toast.success('動画を挿入しました');
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(
        `動画のアップロードに失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleInsertYoutube = () => {
    const url = youtubeUrl.trim();
    if (!url) return;
    if (!extractYouTubeId(url)) {
      toast.error('有効な YouTube URL を入力してください');
      return;
    }
    editor.commands.setYoutubeVideo({ src: url });
    toast.success('YouTube 動画を挿入しました');
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // アップロード中は誤操作での閉じを防ぐ
        if (uploading) return;
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>動画を挿入</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="upload">
          <TabsList>
            <TabsTrigger value="upload">アップロード</TabsTrigger>
            <TabsTrigger value="youtube">YouTube URL</TabsTrigger>
          </TabsList>

          {/* アップロードタブ */}
          <TabsContent value="upload">
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleUpload(f);
                }}
              />
              {uploading ? (
                <div className="p-2 bg-[var(--bg-elevated)] rounded-sm">
                  <div className="h-1.5 bg-[var(--border)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--accent)] transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">
                    アップロード中 {progress}%
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="size-3.5 mr-1" />
                  動画ファイルを選択
                </Button>
              )}
              <p className="text-xs text-[var(--text-muted)]">
                最大 100MB。MP4 など一般的な動画形式に対応しています。
              </p>
            </div>
          </TabsContent>

          {/* YouTube タブ */}
          <TabsContent value="youtube">
            <div className="space-y-3">
              <div>
                <Label>YouTube URL</Label>
                <Input
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      handleInsertYoutube();
                    }
                  }}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleInsertYoutube}
                  disabled={!youtubeUrl.trim()}
                >
                  挿入
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
          >
            閉じる
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
